import React, { useEffect, useState } from 'react';

import queryString from 'query-string';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Stepper from '@material-ui/core/Stepper';

import { loadHrisIntegrationResponse } from './actions/load-hris-integration-response';
import {
  hrisIntegrationResponseForOrgIdSelector,
  hrisIntegrationResponsesStateForOrgIdSelector,
} from './hris-integration-response-selectors';
import { loadHrisIntegrationTypes } from '../merge-integration/actions/load-hris-integration-types';
import { orgsForAccountId } from '../reducers/org-selectors';

import { UiButton } from '../ui/ui-button';
import { UiDialog } from '../ui/ui-dialog';

import { HrisIntegrationResponseIntegrationSelect } from './hris-integration-response-integration-select';
import { HrisIntegrationResponseScheduleMeeting } from './hris-integration-response-schedule-meeting';
import { HrisIntegrationResponseSync } from './hris-integration-response-sync';
import { HrisIntegrationResponseUnsupportedDetails } from './hris-integration-response-unsupported-details';

import { trackWithSmartlook } from '../util/track-with-smartlook';
import { LoadingMessage } from '../widgets/loading-message';

type InputProps = {
  className?: string;
  org: Org;
  buttonSize?: 'default' | 'xxs' | 'xs' | 'sm' | 'lg';
  buttonIntegrationTextOverride?: string;
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>, { org }: InputProps) => ({
  actions: {
    loadHrisIntegrationResponse: () => dispatch(loadHrisIntegrationResponse(org.org_id)),
    loadHrisIntegrationTypes: () => dispatch(loadHrisIntegrationTypes()),
  },
});

export const HrisIntegrationResponseDialog = ({
  className,
  org,
  buttonSize,
  buttonIntegrationTextOverride,
}: InputProps) => {
  const location = useLocation();
  const queryParams = queryString.parse(location.search);

  const hrisIntegrationResponse = useSelector((state: any) =>
    hrisIntegrationResponseForOrgIdSelector(state, org.org_id),
  );
  const hrisIntegrationResponseState = useSelector((state: any) =>
    hrisIntegrationResponsesStateForOrgIdSelector(state, org.org_id),
  );

  const orgs = useSelector((state: any) => orgsForAccountId(state, org.accountId));
  const hasMultipleOrgs = orgs ? orgs.length > 1 : false;

  const [dialogVisible, setDialogVisible] = useState<boolean>(Boolean(queryParams.openHrisDialog));
  const [wizardStep, setWizardStep] = useState<number>(0);

  const { actions } = mapDispatchToProps(useDispatch(), { org });

  // based on the hrisIntegrationResponse, infers where the user was last in the wizard
  const getInferredWizardStep = () => {
    // user hasn't saved anything in the wizard, route them to first step
    if (!hrisIntegrationResponse) {
      return 0;
    }

    if (
      // user has selected that they use a proofed integration
      (hrisIntegrationResponse.HrisIntegrationType &&
        hrisIntegrationResponse.HrisIntegrationType.proofedAt) ||
      // user has selected that they do not use a proofed integration
      // but not that they have used an unproofed or unsupported integration
      (hrisIntegrationResponse.HrisIntegrationType === null &&
        !hrisIntegrationResponse.unsupportedIntegrationName)
    ) {
      // route them to the second step
      return 1;
    }
    if (
      // user has selected that they use an unproofed integration
      (hrisIntegrationResponse.HrisIntegrationType &&
        hrisIntegrationResponse.HrisIntegrationType.proofedAt === null) ||
      // user has indicated that they use an unsupported integration
      (hrisIntegrationResponse.HrisIntegrationType === null &&
        hrisIntegrationResponse.unsupportedIntegrationName)
    ) {
      return 2;
    }

    // should never get here given the state transitions in the dialog but
    // default for type safety
    return 0;
  };

  useEffect(() => {
    actions.loadHrisIntegrationResponse();
    actions.loadHrisIntegrationTypes();
  }, [actions]);

  const handleDialogOpenClick = () => {
    setDialogVisible(true);

    const inferredWizardStep = getInferredWizardStep();
    if (inferredWizardStep !== wizardStep) {
      setWizardStep(inferredWizardStep);
    }
  };

  const getSmartlookClickInfo = (action: string) => ({
    eventName: 'HRIS response dialog click',
    props: {
      action,
    },
  });

  const handleDialogCloseClick = () => {
    setWizardStep(0);
    setDialogVisible(false);
    trackWithSmartlook(getSmartlookClickInfo('close'));
  };

  const handleNext = () => {
    setWizardStep(wizardStep + 1);
  };

  const handlePrevious = () => {
    setWizardStep(wizardStep - 1);
  };

  const getProofedIntegrationStep = () =>
    !hasMultipleOrgs || hrisIntegrationResponse?.isTwoStepSetupEnabled
      ? () => (
          <HrisIntegrationResponseSync
            hrisIntegrationResponse={hrisIntegrationResponse}
            org={org}
            handlePrevious={handlePrevious}
            handleClose={handleDialogCloseClick}
          />
        )
      : () => (
          <HrisIntegrationResponseScheduleMeeting
            handlePrevious={handlePrevious}
            handleClose={handleDialogCloseClick}
          />
        );

  const getUnproofedIntegrationSteps = () => {
    // the second step will always be the unproofed integration select
    const stepsToAdd = [
      () => (
        <HrisIntegrationResponseIntegrationSelect
          selectProofed={false}
          hrisIntegrationResponse={hrisIntegrationResponse}
          org={org}
          handleNext={handleNext}
          handlePrevious={handlePrevious}
        />
      ),
    ];

    if (hrisIntegrationResponse.HrisIntegrationType) {
      // user has selected that they use an unproofed integration - if two-step
      // setup is enabled let them sync, else have them schedule a meeting
      if (hrisIntegrationResponse?.isTwoStepSetupEnabled) {
        stepsToAdd.push(() => (
          <HrisIntegrationResponseSync
            hrisIntegrationResponse={hrisIntegrationResponse}
            org={org}
            handlePrevious={handlePrevious}
            handleClose={handleDialogCloseClick}
          />
        ));
      } else {
        stepsToAdd.push(() => (
          <HrisIntegrationResponseScheduleMeeting
            handlePrevious={handlePrevious}
            handleClose={handleDialogCloseClick}
          />
        ));
      }
    } else {
      // user has selected that they use an unsupported integration
      stepsToAdd.push(() => (
        <HrisIntegrationResponseUnsupportedDetails
          hrisIntegrationResponse={hrisIntegrationResponse}
          org={org}
          handlePrevious={handlePrevious}
          handleClose={handleDialogCloseClick}
        />
      ));
    }

    return stepsToAdd;
  };

  /**
   * The wizard steps are conditional on the state of the HrisIntegrationResponse.
   * The flow is described in a diagram on the HRIS survey subsection of the docs:
   * https://teamengine.atlassian.net/wiki/spaces/ENG/pages/3088482335/HRIS+Overview#HRIS-survey
   */
  const wizardSteps = [
    () => (
      <HrisIntegrationResponseIntegrationSelect
        selectProofed
        hrisIntegrationResponse={hrisIntegrationResponse}
        org={org}
        handleNext={handleNext}
      />
    ),
  ];

  if (hrisIntegrationResponse === undefined) {
    // if the user hasn't saved a response yet, we're not sure what the next
    // step should be, though we know there should be at least one. push a dummy
    // step onto the list to render the stepper accordingly
    wizardSteps.push(() => <div />);
  } else if (
    // user has selected that they use a proofed integration
    hrisIntegrationResponse.HrisIntegrationType &&
    hrisIntegrationResponse.HrisIntegrationType?.proofedAt
  ) {
    wizardSteps.push(getProofedIntegrationStep());
  } else {
    wizardSteps.push(...getUnproofedIntegrationSteps());
  }

  const CurrentComponent = hrisIntegrationResponseState?.loading
    ? () => <LoadingMessage />
    : wizardSteps[wizardStep];

  return (
    <>
      <UiButton
        variant="secondary"
        className={className}
        onClick={handleDialogOpenClick}
        size={buttonSize}
        smartlookEvent={getSmartlookClickInfo('open')}
      >
        Sync with {buttonIntegrationTextOverride || 'HR/Payroll'}
      </UiButton>
      {dialogVisible && (
        <UiDialog title="Sync with HR/Payroll" size="sm" onClose={handleDialogCloseClick}>
          <UiDialog.Content>
            <Stepper activeStep={wizardStep} alternativeLabel>
              {wizardSteps.map((_, i) => (
                // this rule is safe to ignore here because the order of our array will never change
                // eslint-disable-next-line react/no-array-index-key
                <Step key={i}>
                  <StepLabel />
                </Step>
              ))}
            </Stepper>
            <CurrentComponent />
          </UiDialog.Content>
        </UiDialog>
      )}
    </>
  );
};
